import { useTranslation } from 'react-i18next'
import type { ErpVorzeigeProps } from '../registry'

/** Der Fachteil, wie er eingefroren wird (AP-49 6.1) — gezeichnet wird aus ihm, nicht aus dem Formular. */
export interface KanonBesprechung {
  form: 'PROTOKOLL' | 'BERICHTIGUNG'
  art: string
  laufnummer: number
  beginn?: string | null
  ende?: string | null
  ort?: string | null
  leitung?: string | null
  teilnehmer: { name: string; firma: string | null; funktion: string | null; anwesenheit: 'ANWESEND' | 'ENTSCHULDIGT' | 'ABWESEND' | null; verteiler: boolean }[]
  tagesordnung?: { key: string; titel: string }[]
  punkte?: {
    key: string
    nummer: string
    top: string | null
    art: 'INFO' | 'BESCHLUSS' | 'AUFGABE'
    text: string
    zustaendig: { name: string; firma: string | null } | null
    termin: { datum: string | null; text: string | null } | null
    status: 'OFFEN' | 'ERLEDIGT' | 'ENTFAELLT' | null
    herkunft: string | null
  }[]
  naechste?: { datum: string; beginn: string | null; ort: string | null } | null
  einspruch?: { tage: number; text: string } | null
  berichtigungen?: { von: string; eingangAm: string; punkt: string | null; einwand: string; berichtigung: string | null }[]
  bemerkung: string | null
}

const datum = (iso: string | null | undefined) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '')
const KUERZEL = { INFO: 'I', BESCHLUSS: 'B', AUFGABE: 'A' } as const

/**
 * AP-49 11.1, 11.3 — **Vorzeige und eingefrorener Stand der Besprechung im ERP**: wie das PDF —
 * Kopf, Teilnehmer, Punkte nach Tagesordnung, nächste Besprechung, Einspruchsfrist, Verteiler; bei der
 * Berichtigung die Einwände.
 */
export function BesprechungErpVorzeige({ fachteil }: ErpVorzeigeProps) {
  const { t } = useTranslation()
  const roh = fachteil as unknown as Partial<KanonBesprechung>
  // Ein unvollständiger Fachteil (ältere Fassung, Platzhalter) zeichnet leer statt abzubrechen.
  const f = { ...roh, teilnehmer: roh.teilnehmer ?? [], tagesordnung: roh.tagesordnung ?? [], punkte: roh.punkte ?? [], berichtigungen: roh.berichtigungen ?? [], bemerkung: roh.bemerkung ?? null } as KanonBesprechung
  const verteiler = f.teilnehmer.filter((p) => p.verteiler)
  const zelle = 'px-2 py-1 align-top'
  const kopf = 'px-2 py-1 text-left text-xs font-medium text-muted-foreground'

  if (f.form === 'BERICHTIGUNG') {
    return (
      <div className="space-y-3 text-sm">
        <h4 className="font-medium">{t('siteDocs.meeting.correctionOf', { art: f.art, nr: f.laufnummer })}</h4>
        <table className="w-full rounded-md border text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className={kopf}>{t('siteDocs.meeting.objectionFrom')}</th>
              <th className={kopf}>{t('siteDocs.meeting.objectionReceived')}</th>
              <th className={kopf}>{t('siteDocs.meeting.objectionItem')}</th>
              <th className={kopf}>{t('siteDocs.meeting.objection')}</th>
              <th className={kopf}>{t('siteDocs.meeting.correction')}</th>
            </tr>
          </thead>
          <tbody>
            {(f.berichtigungen ?? []).map((e, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className={zelle}>{e.von}</td>
                <td className={zelle}>{datum(e.eingangAm)}</td>
                <td className={zelle}>{e.punkt ?? ''}</td>
                <td className={`${zelle} whitespace-pre-line`}>{e.einwand}</td>
                <td className={`${zelle} whitespace-pre-line`}>{e.berichtigung ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {f.bemerkung && <p className="whitespace-pre-line">{f.bemerkung}</p>}
        {verteiler.length > 0 && (
          <p>
            <span className="text-muted-foreground">{t('siteDocs.meeting.distributionList')}:</span> {verteiler.map((p) => [p.name, p.firma].filter(Boolean).join(', ')).join(' · ')}
          </p>
        )}
      </div>
    )
  }

  const tops = f.tagesordnung ?? []
  const punkte = f.punkte ?? []
  const gruppen = [
    ...tops.map((x, i) => ({ key: x.key, titel: `${i + 1} ${x.titel}`, liste: punkte.filter((p) => p.top === x.key) })),
    { key: '', titel: tops.length ? t('siteDocs.meeting.furtherItems') : '', liste: punkte.filter((p) => !p.top) },
  ].filter((g) => g.liste.length || (g.key && tops.length))
  return (
    <div className="space-y-3 text-sm">
      <h4 className="font-medium">{t('siteDocs.meeting.minutesOf', { art: f.art, nr: f.laufnummer })}</h4>
      <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-0.5">
        {f.beginn && (
          <>
            <dt className="text-muted-foreground">{t('siteDocs.meeting.time')}</dt>
            <dd>
              {f.beginn}
              {f.ende ? `–${f.ende}` : ''}
            </dd>
          </>
        )}
        {f.ort && (
          <>
            <dt className="text-muted-foreground">{t('siteDocs.meeting.place')}</dt>
            <dd>{f.ort}</dd>
          </>
        )}
        {f.leitung && (
          <>
            <dt className="text-muted-foreground">{t('siteDocs.meeting.chair')}</dt>
            <dd>{f.leitung}</dd>
          </>
        )}
      </dl>
      {f.teilnehmer.length > 0 && (
        <table className="w-full rounded-md border text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className={kopf}>{t('siteDocs.meeting.name')}</th>
              <th className={kopf}>{t('siteDocs.meeting.company')}</th>
              <th className={kopf}>{t('siteDocs.meeting.function')}</th>
              <th className={kopf}>{t('siteDocs.meeting.attendance')}</th>
              <th className={kopf} title={t('siteDocs.meeting.distribution')}>
                V
              </th>
            </tr>
          </thead>
          <tbody>
            {f.teilnehmer.map((p, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className={zelle}>{p.name}</td>
                <td className={zelle}>{p.firma ?? ''}</td>
                <td className={zelle}>{p.funktion ?? ''}</td>
                <td className={zelle}>{p.anwesenheit ? t(`siteDocs.meeting.attendanceFach.${p.anwesenheit}`) : ''}</td>
                <td className={zelle}>{p.verteiler ? '✓' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {gruppen.map((g) => (
        <section key={g.key || 'ohne'} className="space-y-1">
          {g.titel && <h5 className="font-medium">{g.titel}</h5>}
          {g.liste.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.noItems')}</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {g.liste.map((p) => (
                <li key={p.key} className="flex flex-wrap gap-x-3 px-3 py-1.5">
                  <span className="w-12 shrink-0 tabular-nums text-muted-foreground">{p.nummer}</span>
                  <span className="w-5 shrink-0 font-medium" title={t(`siteDocs.meeting.kindFach.${p.art}`)}>
                    {KUERZEL[p.art]}
                  </span>
                  <span className="min-w-0 flex-1 whitespace-pre-line">
                    {p.text}
                    {p.herkunft && <span className="ml-2 text-xs text-muted-foreground">{p.herkunft}</span>}
                  </span>
                  {p.art === 'AUFGABE' && (
                    <span className="text-xs text-muted-foreground">
                      {[p.zustaendig && [p.zustaendig.name, p.zustaendig.firma].filter(Boolean).join(', '), p.termin && [datum(p.termin.datum), p.termin.text].filter(Boolean).join(' · '), p.status && t(`siteDocs.meeting.stateFach.${p.status}`)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
      {f.naechste && (
        <p>
          <span className="text-muted-foreground">{t('siteDocs.meeting.next')}:</span> {datum(f.naechste.datum)}
          {f.naechste.beginn ? `, ${f.naechste.beginn}` : ''}
          {f.naechste.ort ? `, ${f.naechste.ort}` : ''}
        </p>
      )}
      {f.bemerkung && <p className="whitespace-pre-line">{f.bemerkung}</p>}
      {f.einspruch && (
        <p className="text-muted-foreground">
          {t('siteDocs.meeting.objectionPeriod')}: {f.einspruch.text}
        </p>
      )}
      {verteiler.length > 0 && (
        <p>
          <span className="text-muted-foreground">{t('siteDocs.meeting.distributionList')}:</span> {verteiler.map((p) => [p.name, p.firma].filter(Boolean).join(', ')).join(' · ')}
        </p>
      )}
    </div>
  )
}
